import type { JSX } from 'solid-js';
import {
  GetConfigurationResponse,
  IssueResponse,
} from '@data-access/connect/tc-issue-management';
import { translate } from '@fulcrum/lib';
import { getUserNameByTiduuid, type IProjectUser } from '../../utils/util';
import type { IssueIcon } from '../../utils/issue-icons';
import { IssueTypeBadge } from '../IssueTypeBadge/IssueTypeBadge';
import { IssuePriorityBadge } from '../IssuePriorityBadge/IssuePriorityBadge';
import { IssueStatusBadge } from '../IssueStatusBadge/IssueStatusBadge';
import { IssueThumbnail } from '../IssueThumbnail/IssueThumbnail';
import { CustomTooltip } from '@tcweb/connect-ui';

export interface IssueCardProps {
  issue: IssueResponse;
  onRowClick?(issue: IssueResponse): void;
  projectUsersData?: readonly IProjectUser[];
  configurationData?: GetConfigurationResponse;
  /** Normalized list of default + custom icons for type icon rendering */
  iconsList?: readonly IssueIcon[];
  projectId: string;
  contextId: string;
  accessToken?: string;
}

export function IssueCard(props: IssueCardProps): JSX.Element {
  const handleRowClick = (): void => {
    props.onRowClick?.(props.issue);
  };

  return (
    <div
      class="cursor-pointer"
      onClick={handleRowClick}
      style="
        height: 169px;
        overflow: hidden;
        border: 1px solid #e5e7eb;
        transition: background-color 0.2s ease, border-left 0.2s ease;
      "
      onMouseEnter={(e): void => {
        const target = e.currentTarget as HTMLElement;
        target.style.backgroundColor = '#e5f0f8';
        target.style.borderLeft = '.25rem solid #0c77be';
      }}
      onMouseLeave={(e): void => {
        const target = e.currentTarget as HTMLElement;
        target.style.backgroundColor = '';
        target.style.borderLeft = '';
      }}
    >
      <div
        class="w-full h-full bg-white flex flex-col"
        style="overflow: hidden;"
      >
        {/* Content area */}
        <div class="flex-1 px-2 pt-2 overflow-hidden">
          <div class="flex gap-2 items-start">
            {/* Thumbnail */}
            <IssueThumbnail
              issueId={props.issue.id}
              projectId={props.projectId}
              contextId={props.contextId}
              accessToken={props.accessToken}
            />

            <div class="flex-1 min-w-0">
              {/* Header: ID + Status */}
              <div class="flex items-center justify-between gap-1">
                <span
                  class="font-medium text-xs flex-shrink-0"
                  style={{ color: '#0c77be' }}
                >
                  {props.issue.serverAssignedId}
                </span>
                <IssueStatusBadge
                  status={props.issue.status}
                  configuration={props.configurationData}
                />
              </div>

              {/* Title */}
              <h3 class="text-sm line-clamp-2 overflow-hidden text-ellipsis leading-relaxed">
                {(() => {
                  const MAX_TITLE_LENGTH = 100;
                  return props.issue.title &&
                    props.issue.title.length > MAX_TITLE_LENGTH
                    ? `${props.issue.title.substring(0, MAX_TITLE_LENGTH)}…`
                    : props.issue.title;
                })()}
              </h3>
            </div>
          </div>

          {/* Badges row (Type, Priority) */}
          {(props.issue.type || props.issue.priority) && (
            <div class="flex items-center justify-between mt-1">
              <IssueTypeBadge
                type={props.issue.type}
                configuration={props.configurationData}
                iconsList={props.iconsList}
              />
              <IssuePriorityBadge
                priority={props.issue.priority}
                configuration={props.configurationData}
              />
            </div>
          )}
        </div>

        {/* Footer per Figma: user and date - sticks to bottom */}
        <div
          class="flex items-center justify-between px-2 py-2 bg-gray-100"
          style="border-top: 1px solid #e5e7eb;"
        >
          <div class="flex items-center gap-2 flex-1 min-w-0">
            <i class="icon-font tc-icon-user text-gray-500 i16 flex-shrink-0" />
            <CustomTooltip
              text={props.issue.assignees
                ?.map((assignee) =>
                  getUserNameByTiduuid(
                    assignee.split(':')[1],
                    props.projectUsersData
                  )
                )
                .join(', ')}
              position="top-start"
              class="flex-1 min-w-0"
            >
              <div class="flex items-center gap-1 min-w-0">
                <span class="text-xs text-semibold truncate">
                  {(() => {
                    const assignees = props.issue.assignees || [];
                    if (assignees.length === 0)
                      return translate('TC.Common.Unassigned');

                    const firstAssignee = assignees[0];
                    const firstName = firstAssignee
                      ? getUserNameByTiduuid(
                          firstAssignee.split(':')[1],
                          props.projectUsersData
                        )
                      : translate('TC.Common.Unassigned');
                    return firstName;
                  })()}
                </span>
                <span class="text-xs text-semibold mr-1">
                  {props.issue.assignees &&
                    props.issue.assignees.length > 1 &&
                    ` +${props.issue.assignees.length - 1}`}
                </span>
              </div>
            </CustomTooltip>
          </div>
          {props.issue.dueDate &&
          new Date(props.issue.dueDate).toString() !== 'Invalid Date' ? (
            <div class="flex items-center gap-2 w-26 flex-shrink-0">
              <i class="icon-font tc-icon-date-range text-gray-500 i16" />
              <span class="text-xs text-semibold">
                {new Date(props.issue.dueDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
